export interface BrainJourneyStats {
  total: number;
  wins: number;
  losses: number;
}

export interface BrainSummarySection {
  id: string;
  title: string;
  tone: 'win' | 'loss' | 'neutral' | 'caveat';
  paragraphs: string[];
  bullets: string[];
}

export interface ParsedBrainSummary {
  journeyStats?: BrainJourneyStats;
  sections: BrainSummarySection[];
}

const SECTION_MARKERS: { pattern: RegExp; id: string; title: string; tone: BrainSummarySection['tone'] }[] = [
  { pattern: /\s+Losses cluster/i, id: 'losses', title: 'Loss patterns', tone: 'loss' },
  { pattern: /\s+On the new proposal-engagement signal:/i, id: 'engagement', title: 'Proposal engagement signal', tone: 'caveat' },
  { pattern: /\s+Pillar-wise,/i, id: 'pillars', title: 'Strategic pillars', tone: 'neutral' },
];

function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+(?=[A-Z(])/)
    .map(s => s.trim())
    .filter(Boolean);
}

function extractNumberedBullets(text: string): { bullets: string[]; remainder: string } {
  const bullets: string[] = [];
  const block = text.match(/:\s*\(1\)/);
  if (block) {
    const idx = text.indexOf('(1)');
    const intro = text.slice(0, idx).trim();
    const listPart = text.slice(idx);
    let lm: RegExpExecArray | null;
    const listRe = /\(\d+\)\s*([\s\S]*?)(?=\(\d+\)|$)/g;
    while ((lm = listRe.exec(listPart)) !== null) {
      bullets.push(lm[1].replace(/\.\s*$/, '').trim());
    }
    return { bullets, remainder: intro };
  }
  return { bullets, remainder: text };
}

export function parseBrainSummary(summary: string): ParsedBrainSummary {
  const trimmed = summary.trim();
  if (!trimmed) return { sections: [] };

  const statsMatch = trimmed.match(
    /Across\s+(\d+)\s+journeys\s*\(~?\s*(\d+)\s+wins,\s*~?\s*(\d+)\s+losses\)/i,
  );
  const journeyStats = statsMatch
    ? {
        total: Number(statsMatch[1]),
        wins: Number(statsMatch[2]),
        losses: Number(statsMatch[3]),
      }
    : undefined;

  let rest = trimmed;
  if (statsMatch) {
    rest = trimmed.slice(statsMatch.index! + statsMatch[0].length).trim();
    if (rest.startsWith(',')) rest = rest.slice(1).trim();
  }

  const sections: BrainSummarySection[] = [];

  if (rest) {
    let winsBody = rest;
    for (const marker of SECTION_MARKERS) {
      const idx = winsBody.search(marker.pattern);
      if (idx >= 0) {
        winsBody = winsBody.slice(0, idx).trim();
        break;
      }
    }
    if (winsBody) {
      sections.push({
        id: 'wins',
        title: 'What wins look like',
        tone: 'win',
        paragraphs: splitSentences(winsBody),
        bullets: [],
      });
    }
  }

  for (let i = 0; i < SECTION_MARKERS.length; i++) {
    const marker = SECTION_MARKERS[i];
    const start = trimmed.search(marker.pattern);
    if (start < 0) continue;
    const nextStarts = SECTION_MARKERS.slice(i + 1)
      .map(m => trimmed.search(m.pattern))
      .filter(n => n > start);
    const end = nextStarts.length ? Math.min(...nextStarts) : trimmed.length;
    let chunk = trimmed.slice(start, end).trim();
    chunk = chunk.replace(marker.pattern, '').trim();
    if (chunk.startsWith(':')) chunk = chunk.slice(1).trim();

    const { bullets, remainder } = extractNumberedBullets(chunk);
    const body = remainder || chunk;
    const paragraphs = bullets.length ? splitSentences(body).filter(p => !p.match(/^\(\d+\)/)) : splitSentences(body);

    sections.push({
      id: marker.id,
      title: marker.title,
      tone: marker.tone,
      paragraphs,
      bullets,
    });
  }

  if (sections.length === 0 && trimmed) {
    sections.push({
      id: 'full',
      title: 'Analysis',
      tone: 'neutral',
      paragraphs: splitSentences(trimmed),
      bullets: [],
    });
  }

  return { journeyStats, sections };
}
